import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchOrganisationSocialApprovalRequest,
  fetchOrganisationSocialConnections,
  fetchOrganisationSocialDisconnect,
  fetchOrganisationSocialSync,
  updateOrganisationXSocialSettings,
} from '../../services/organisation.service';
import {
  ISocialAccountConnection,
  ISocialConnectionState,
  TSocialPlatform,
} from '../../types/@organisation';

type Props = {
  organisationId: string;
  callbackMessage?: string;
};

const ORDER: TSocialPlatform[] = ['instagram', 'tiktok', 'x'];

const DISPLAY_NAMES: Record<TSocialPlatform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  x: 'X',
};

function PlatformLogo({ platform, size = 40 }: { platform: TSocialPlatform; size?: number }) {
  const iconSize = Math.round(size * 0.55);

  if (platform === 'instagram') {
    return (
      <Box
        aria-hidden
        sx={{
          width: size,
          height: size,
          borderRadius: '12px',
          background: 'linear-gradient(45deg, #f9ce34 0%, #ee2a7b 52%, #6228d7 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none">
          <rect x="3" y="3" width="18" height="18" rx="5" stroke="#fff" strokeWidth="2" />
          <circle cx="12" cy="12" r="4.2" stroke="#fff" strokeWidth="2" />
          <circle cx="17.4" cy="6.6" r="1.15" fill="#fff" />
        </svg>
      </Box>
    );
  }

  if (platform === 'tiktok') {
    return (
      <Box
        aria-hidden
        sx={{
          width: size,
          height: size,
          borderRadius: '12px',
          backgroundColor: '#111111',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <svg width={iconSize} height={iconSize} viewBox="0 0 24 24">
          <path
            fill="#25F4EE"
            d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.77.14 2.89 2.89 0 0 1 2.31-3.08v-3.54a6.37 6.37 0 0 0-1-.08A6.34 6.34 0 0 0 1.57 15.5a6.34 6.34 0 0 0 10.76 4.55 6.33 6.33 0 0 0 1.87-4.52V8.95a8.16 8.16 0 0 0 4.77 1.52V7.03a4.84 4.84 0 0 1-.38-.34Z"
            transform="translate(0.6 0.6)"
          />
          <path
            fill="#FE2C55"
            d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.77.14 2.89 2.89 0 0 1 2.31-3.08v-3.54a6.37 6.37 0 0 0-1-.08A6.34 6.34 0 0 0 1.57 15.5a6.34 6.34 0 0 0 10.76 4.55 6.33 6.33 0 0 0 1.87-4.52V8.95a8.16 8.16 0 0 0 4.77 1.52V7.03a4.84 4.84 0 0 1-.38-.34Z"
            transform="translate(-0.6 -0.6)"
          />
          <path
            fill="#fff"
            d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.77.14 2.89 2.89 0 0 1 2.31-3.08v-3.54a6.37 6.37 0 0 0-1-.08A6.34 6.34 0 0 0 1.57 15.5a6.34 6.34 0 0 0 10.76 4.55 6.33 6.33 0 0 0 1.87-4.52V8.95a8.16 8.16 0 0 0 4.77 1.52V7.03a4.84 4.84 0 0 1-.38-.34Z"
          />
        </svg>
      </Box>
    );
  }

  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        borderRadius: '12px',
        backgroundColor: '#111111',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg width={iconSize} height={iconSize} viewBox="0 0 24 24">
        <path
          fill="#fff"
          d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.727-8.835L1.254 2.25H8.08l4.253 5.622L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z"
        />
      </svg>
    </Box>
  );
}

const getErrorMessage = (error: any, fallback: string): string => {
  const responseMessage = error?.response?.data?.message;

  if (Array.isArray(responseMessage)) {
    return responseMessage.join(', ');
  }

  return responseMessage || error?.message || fallback;
};

const normaliseAccountHandle = (value?: string | null): string => {
  return String(value || '')
    .trim()
    .replace(/^@+/, '');
};

const formatCount = (value?: number | null): string => {
  if (value == null || !Number.isFinite(value)) return '';
  return new Intl.NumberFormat().format(value);
};

const formatTimestamp = (value?: number | null): string => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return '';
  }
};

const isTokenExpiring = (value?: number | null): boolean => {
  if (!value) return false;
  return value - Date.now() < 7 * 24 * 60 * 60 * 1000;
};

const profileUrl = (platform: TSocialPlatform, handle?: string): string => {
  if (!handle) return '';
  if (platform === 'instagram') return `https://www.instagram.com/${encodeURIComponent(handle)}/`;
  if (platform === 'tiktok') return `https://www.tiktok.com/@${encodeURIComponent(handle)}`;
  return `https://x.com/${encodeURIComponent(handle)}`;
};

const websiteUrl = (value?: string | null): string => {
  const website = String(value || '').trim();
  if (!website) return '';
  return /^https?:\/\//i.test(website) ? website : `https://${website}`;
};

function AccountStat({ label, value }: { label: string; value: string }) {
  if (!value) return null;
  return (
    <Box
      sx={{
        minWidth: 88,
        px: 1.5,
        py: 1,
        border: '1px solid #e3e8ef',
        borderRadius: '12px',
        background: '#fafbfd',
      }}
    >
      <Typography sx={{ fontWeight: 700, color: '#17324d', fontSize: 16, lineHeight: 1.2 }}>
        {value}
      </Typography>
      <Typography sx={{ color: '#7c8b99', fontSize: 12, mt: 0.25 }}>{label}</Typography>
    </Box>
  );
}

export default function OrganisationSocialsPanel({
  organisationId,
  callbackMessage = '',
}: Props) {
  const [state, setState] =
    useState<ISocialConnectionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyPlatform, setBusyPlatform] =
    useState<TSocialPlatform | null>(null);
  const [message, setMessage] = useState(callbackMessage);
  const [clubXId, setClubXId] = useState('');

  const load = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);

      const response: any =
        await fetchOrganisationSocialConnections(organisationId);

      setState(response?.data || response);
    } catch (error: any) {
      setMessage(
        getErrorMessage(error, 'Unable to load social accounts.'),
      );
    } finally {
      setLoading(false);
    }
  }, [organisationId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (callbackMessage) {
      setMessage(callbackMessage);
    }
  }, [callbackMessage]);

  const accounts = useMemo<
    Partial<Record<TSocialPlatform, ISocialAccountConnection>>
  >(() => {
    const result: Partial<
      Record<TSocialPlatform, ISocialAccountConnection>
    > = {};

    for (const account of state?.platforms || []) {
      if (ORDER.includes(account.platform)) {
        result[account.platform] = account;
      }
    }

    return result;
  }, [state?.platforms]);

  const isAdmin = state?.myRole === 'admin';

  useEffect(() => {
    setClubXId(normaliseAccountHandle(accounts.x?.clubXId));
  }, [accounts.x?.clubXId]);

  const isConnected = (
    account?: ISocialAccountConnection,
  ): boolean => {
    return account?.status === 'connected';
  };

  const syncAccount = async (platform: TSocialPlatform): Promise<void> => {
    try {
      setBusyPlatform(platform);
      setMessage('');
      await fetchOrganisationSocialSync(organisationId, platform);
      setMessage(`${DISPLAY_NAMES[platform]} account details refreshed.`);
      await load();
    } catch (error: any) {
      setMessage(
        getErrorMessage(
          error,
          `Unable to refresh ${DISPLAY_NAMES[platform]}.`,
        ),
      );
    } finally {
      setBusyPlatform(null);
    }
  };

  const authorise = async (
    platform: TSocialPlatform,
  ): Promise<void> => {
    try {
      setBusyPlatform(platform);
      setMessage('');

      const response: any =
        await fetchOrganisationSocialApprovalRequest(
          organisationId,
          platform,
        );

      const payload = response?.data || response;

      if (!payload?.approvalUrl) {
        throw new Error('No approval URL was returned.');
      }

      window.location.assign(payload.approvalUrl);
    } catch (error: any) {
      setMessage(
        getErrorMessage(
          error,
          `Unable to connect ${DISPLAY_NAMES[platform]}.`,
        ),
      );
      setBusyPlatform(null);
    }
  };

  const disconnect = async (
    platform: TSocialPlatform,
  ): Promise<void> => {
    const displayName = DISPLAY_NAMES[platform];

    if (
      !window.confirm(
        `Disconnect the organisation's ${displayName} account?`,
      )
    ) {
      return;
    }

    try {
      setBusyPlatform(platform);
      setMessage('');

      await fetchOrganisationSocialDisconnect(
        organisationId,
        platform,
      );

      setMessage(`${displayName} disconnected.`);
      await load();
    } catch (error: any) {
      setMessage(
        getErrorMessage(
          error,
          `Unable to disconnect ${displayName}.`,
        ),
      );
    } finally {
      setBusyPlatform(null);
    }
  };

  const saveClubXId = async (): Promise<void> => {
    try {
      setBusyPlatform('x');
      setMessage('');
      await updateOrganisationXSocialSettings(organisationId, { clubXId });
      setMessage('Club X ID saved.');
      await load();
    } catch (error: any) {
      setMessage(getErrorMessage(error, 'Unable to save the Club X ID.'));
    } finally {
      setBusyPlatform(null);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          py: 6,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box>
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 700,
            color: '#17324d',
          }}
        >
          Social accounts
        </Typography>

        <Typography sx={{ mt: 1, color: '#5f7285' }}>
          Authorise the club accounts that event administrators may
          use for direct posting.
        </Typography>
      </Box>

      {message && (
        <Alert
          severity={
            message.toLowerCase().includes('success') ||
            message.toLowerCase().includes('connected') ||
            message.toLowerCase().includes('disconnected')
              ? 'success'
              : 'info'
          }
        >
          {message}
        </Alert>
      )}

      {!isAdmin && (
        <Alert severity="info">
          Only organisation administrators can connect, reconnect or
          disconnect social accounts.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2,minmax(0,1fr))',
          },
          gap: 2,
        }}
      >
        {ORDER.map((platform) => {
          const account = accounts[platform];
          const connected = isConnected(account);
          const supported =
            account?.authorisationSupported === true;
          const canConfigure = isAdmin && supported;
          const busy = busyPlatform === platform;
          const handle = normaliseAccountHandle(
            account?.accountName,
          );

          const mediaTypes = [
            account?.imagePublishingSupported ? 'images' : '',
            account?.reelsPublishingSupported || account?.videoPublishingSupported ? 'reels' : '',
            account?.carouselPublishingSupported ? 'carousels' : '',
            account?.storyPublishingSupported ? 'stories' : '',
          ].filter(Boolean);
          const followerLabel = formatCount(account?.followersCount);
          const followingLabel = formatCount(account?.followsCount);
          const mediaLabel = formatCount(account?.mediaCount);
          const lastSynced = formatTimestamp(account?.lastSyncedAt);
          const tokenExpiring = connected && isTokenExpiring(account?.tokenExpiresAt);
          const href = connected ? profileUrl(platform, handle) : '';
          const site = connected ? websiteUrl(account?.website) : '';
          const showLastError =
            !!account?.lastError &&
            !(
              connected &&
              (account.lastErrorCode === 'replayed_state' ||
                /already been processed/i.test(String(account.lastError)))
            );

          return (
            <Paper
              key={platform}
              elevation={0}
              sx={{
                p: 2.5,
                border: '1px solid #e3e8ef',
                borderRadius: '14px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                  {connected && platform === 'instagram' ? null : (
                    <PlatformLogo platform={platform} />
                  )}
                  {connected && account?.profilePictureUrl ? (
                    <Box
                      component={href ? 'a' : 'span'}
                      href={href || undefined}
                      target={href ? '_blank' : undefined}
                      rel={href ? 'noreferrer' : undefined}
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '1px solid #e3e8ef',
                        flexShrink: 0,
                        display: 'block',
                      }}
                    >
                      <Box
                        component="img"
                        src={account.profilePictureUrl}
                        alt={handle || DISPLAY_NAMES[platform]}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </Box>
                  ) : null}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: '#17324d',
                      }}
                    >
                      {DISPLAY_NAMES[platform]}
                    </Typography>

                    {connected && handle ? (
                      <Link
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                        underline="hover"
                        sx={{
                          mt: 0.5,
                          display: 'inline-block',
                          color: '#1b6ef3',
                          fontWeight: 600,
                        }}
                      >
                        @{handle}
                      </Link>
                    ) : (
                      <Typography sx={{ mt: 0.5, color: '#5f7285' }}>
                        No account connected
                      </Typography>
                    )}
                    {connected && account?.profileName ? (
                      <Typography sx={{ color: '#7c8b99', fontSize: 13 }}>
                        {account.profileName}
                      </Typography>
                    ) : null}
                  </Box>
                </Box>

                <Chip
                  size="small"
                  color={
                    connected
                      ? 'success'
                      : supported
                        ? 'default'
                        : 'warning'
                  }
                  label={
                    connected
                      ? 'Connected'
                      : supported
                        ? 'Not connected'
                        : 'Not available'
                  }
                />
              </Box>

              {connected ? (
                <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {[
                    { label: 'followers', value: followerLabel },
                    { label: 'following', value: followingLabel },
                    { label: 'posts', value: mediaLabel },
                  ].map((stat) =>
                    stat.value ? (
                      <Box
                        key={stat.label}
                        component={href ? 'a' : 'div'}
                        href={href || undefined}
                        target={href ? '_blank' : undefined}
                        rel={href ? 'noreferrer' : undefined}
                        sx={{ textDecoration: 'none', color: 'inherit' }}
                      >
                        <AccountStat label={stat.label} value={stat.value} />
                      </Box>
                    ) : null,
                  )}
                </Box>
              ) : null}

              <Box sx={{ mt: 2 }}>
                {connected && account?.biography ? (
                  <Typography sx={{ color: '#5f7285', mb: 1.5 }}>
                    {account.biography}
                  </Typography>
                ) : null}

                {site ? (
                  <Link
                    href={site}
                    target="_blank"
                    rel="noreferrer"
                    underline="hover"
                    sx={{ display: 'inline-block', mb: connected && platform === 'instagram' ? 0 : 1.5, color: '#1b6ef3', fontWeight: 600 }}
                  >
                    {account?.website}
                  </Link>
                ) : null}

                {connected && platform === 'instagram' ? null : (
                  <>
                    <Typography sx={{ color: '#5f7285' }}>
                      Direct posting:{' '}
                      {account?.canDirectPublish
                        ? 'Available'
                        : connected
                          ? 'Required permission not granted'
                          : supported
                            ? 'Connect account first'
                            : 'Not currently available'}
                    </Typography>

                    <Typography sx={{ mt: 1, color: '#5f7285' }}>
                      Media:{' '}
                      {mediaTypes.length > 0
                        ? mediaTypes.join(', ')
                        : 'Not configured'}
                    </Typography>

                    {connected && account?.accountType ? (
                      <Typography sx={{ mt: 1, color: '#5f7285' }}>
                        Account type: {account.accountType.replace(/_/g, ' ').toLowerCase()}
                      </Typography>
                    ) : null}

                    <Typography sx={{ mt: 1, color: '#5f7285' }}>
                      Insights:{' '}
                      {account?.insightsSupported
                        ? 'Supported'
                        : 'Not currently available'}
                    </Typography>
                  </>
                )}

                {connected && lastSynced ? (
                  <Typography sx={{ mt: 1, color: '#7c8b99', fontSize: 13 }}>
                    Last synced {lastSynced}
                    {account?.healthStatus ? `  -  ${account.healthStatus}` : ''}
                  </Typography>
                ) : null}
              </Box>

              {tokenExpiring && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  This Instagram connection will expire soon. Reconnect to keep publishing and messaging working.
                </Alert>
              )}

              {showLastError && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  {account.lastError}
                </Alert>
              )}

              {platform === 'x' ? (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Club X ID (optional)"
                    value={clubXId}
                    disabled={!isAdmin || busy}
                    onChange={(event) => setClubXId(normaliseAccountHandle(event.target.value))}
                    placeholder="club_handle"
                    helperText="If this matches the connected X account, it will not be repeated in the Post."
                  />
                  <Button
                    sx={{ mt: 1 }}
                    variant="outlined"
                    disabled={!isAdmin || busy}
                    onClick={() => void saveClubXId()}
                  >
                    Save club ID
                  </Button>
                </Box>
              ) : null}

              <Box
                sx={{
                  mt: 2.5,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                }}
              >
                {connected ? (
                  <>
                    <Button
                      variant="contained"
                      disabled={!canConfigure || busy}
                      onClick={() => {
                        void authorise(platform);
                      }}
                    >
                      {busy ? 'Working...' : 'Reconnect'}
                    </Button>

                    {href ? (
                      <Button
                        variant="outlined"
                        href={href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View profile
                      </Button>
                    ) : null}

                    {platform === 'instagram' ? (
                      <Button
                        variant="outlined"
                        disabled={!canConfigure || busy}
                        onClick={() => {
                          void syncAccount(platform);
                        }}
                      >
                        {busy ? 'Refreshing...' : 'Refresh'}
                      </Button>
                    ) : null}

                    <Button
                      color="error"
                      disabled={!isAdmin || busy}
                      onClick={() => {
                        void disconnect(platform);
                      }}
                    >
                      Disconnect
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="contained"
                    disabled={!canConfigure || busy}
                    onClick={() => {
                      void authorise(platform);
                    }}
                  >
                    {busy ? 'Opening...' : `Connect ${DISPLAY_NAMES[platform]}`}
                  </Button>
                )}
              </Box>

              {!supported && (
                <Typography
                  sx={{
                    mt: 1.5,
                    fontSize: 13,
                    color: '#7c8b99',
                  }}
                >
                  Account authorisation is not yet available for this
                  platform.
                </Typography>
              )}
            </Paper>
          );
        })}
      </Box>
    </Box>
  );
}
